import { put, delay, takeEvery, takeLatest, all, call, select, race, cancelled } from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setPicTreeGoldChestStats,
  setPicTreeGoldChestTrend,
  setPicTreeChestTypeOptions,
  setPicTreeRainbowListDetail,
  setPicTreeLatLonByPowerAssetId,
} from "actions";
import { getJwtToken, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import * as H from "helper";

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTree;
export const startPrevDate = (state) => state.page.prevDateRangePicTree[0];
export const endPrevDate = (state) => state.page.prevDateRangePicTree[1];
export const pointExchangePageNumber = (state) => state.pictree.pointExchangePageNumber;
export const selectedSeason = (state) => state.pictree.selectedSeason;
export const chestType = (state) => state.pictree.chestType;

// "all" means all business-allowed rarities configured for selected season(s), not all DB rarities.

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,   // 30s
  retries: 3,         // 合計 4 回までトライ（初回 + リトライ3）
  backoffBase: 1500,  // 1.5s指数バックオフ
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,           // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2,                 // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000,          // 1秒
    backoffFactor = 2,           // 2倍ずつ
    jitter = true,               // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || '';
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    }
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功

    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

export function* getPictreeDataGoldChestTrend(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const selectedSeasonIds = selectedSeasonData
      .map((s) => s?.seasonId)
      .filter(Boolean);
    const chestTypeValue = yield select(chestType);
    const isGold = chestTypeValue === "gold";
    const isAll = chestTypeValue === "all";
    const isSpecificRarity = String(chestTypeValue).startsWith("rarity:");

    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `e.game_space_id = ${ids}` // ids は "'id1'"
          : `e.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const fallbackRarityClause =
      "(e.rarity IN ('GOLD', 'RAINBOW') OR e.rarity LIKE 'SPONSOR%')";
    let allUnionRarities = [];
    let hasUnmappedOrEmpty = selectedSeasonIds.length === 0;

    if (selectedSeasonIds.length > 0) {
      const seasonIdsIn = selectedSeasonIds.map((id) => `'${id}'`).join(", ");
      const sqlQuerySeasonRarities = {
        query: `
          SELECT DISTINCT
            tbps.game_space_id AS season_id,
            tbps.rarity AS target_rarity
          FROM treasure_box_prob_setting tbps
          WHERE tbps.game_space_id IN (${seasonIdsIn})
        `,
        database: "KpiDashboard",
        type: "list",
      };

      try {
        const [rawRarityRows] = yield all([
          call(
            callWithTimeoutAndRetry,
            getDataFromSxi,
            [sqlQuerySeasonRarities, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
            SXI_HEAVY_RETRY
          ),
        ]);
        const rarityRows = Array.isArray(rawRarityRows)
          ? rawRarityRows
          : rawRarityRows?.rows ?? [];

        const rarityBySeason = rarityRows.reduce((acc, row) => {
          const seasonId = row?.season_id;
          const rarity = row?.target_rarity;
          if (!seasonId || !rarity) return acc;
          if (!acc[seasonId]) acc[seasonId] = new Set();
          acc[seasonId].add(rarity);
          return acc;
        }, {});

        allUnionRarities = Array.from(
          new Set(
            selectedSeasonIds.flatMap((id) => Array.from(rarityBySeason[id] || []))
          )
        );
        hasUnmappedOrEmpty = selectedSeasonIds.some(
          (id) => !rarityBySeason[id] || rarityBySeason[id].size === 0
        );
      } catch (error) {
        // rarity master query failure should not block main query; fallback to legacy-compatible filter.
        hasUnmappedOrEmpty = true;
        allUnionRarities = [];
      }
    }
    const dynamicChestTypeOptions = (() => {
      const baseOptions = [
        { name: "全て", value: "all" },
        { name: "金", value: "gold" },
        { name: "レインボー", value: "rainbow" },
      ];
      if (allUnionRarities.length === 0) return baseOptions;
      const rarityOptions = allUnionRarities
        .slice()
        .filter((rarity) => String(rarity).toUpperCase() !== "GOLD")
        .sort((a, b) => String(a).localeCompare(String(b)))
        .map((rarity) => ({ name: rarity, value: `rarity:${rarity}` }));
      return [...baseOptions, ...rarityOptions];
    })();
    yield put(setPicTreeChestTypeOptions(dynamicChestTypeOptions));

    // rarity query
    const rarityQuery = (() => {
      if (isGold) return "AND e.rarity = 'GOLD'";
      if (isSpecificRarity) {
        const selectedRarity = String(chestTypeValue).replace(/^rarity:/, "");
        return `AND e.rarity = '${selectedRarity.replace(/'/g, "''")}'`;
      }
      if (chestTypeValue === "rainbow") {
        return "AND (e.rarity = 'RAINBOW' OR e.rarity LIKE 'SPONSOR%')";
      }
      if (!isAll) return "AND e.rarity LIKE 'SPONSOR%'";

      const allowlistClause = allUnionRarities.length > 0
        ? `e.rarity IN (${allUnionRarities.map((r) => `'${String(r).replace(/'/g, "''")}'`).join(", ")})`
        : "";

      if (allowlistClause && hasUnmappedOrEmpty) {
        return `AND (${allowlistClause} OR ${fallbackRarityClause})`;
      }
      if (allowlistClause) return `AND ${allowlistClause}`;
      return `AND ${fallbackRarityClause}`;
    })();
    const raritySelectQuery = isAll ? "e.rarity AS 'RARITY'," : "";
    const rarityGroupByQuery = isAll ? "e.rarity," : "";
    const rewardJoinClause = isAll
      ? "STRAIGHT_JOIN treasure_box_reward reward ON reward.id = e.treasure_box_reward_id"
      : "JOIN treasure_box_reward reward ON reward.id = e.treasure_box_reward_id";

    const sqlQueryGoldChestTrend = {
      query: `
        SELECT
          DATE(CONVERT_TZ(e.occurred_datetime, '+00:00', '+09:00')) AS DAY,
          ${raritySelectQuery}
          reward.type AS 'REWARD_TYPE',
          CASE WHEN reward.type = 'ITEM' THEN i.code WHEN reward.type = 'REWARD_COIN' THEN NULL ELSE NULL END AS 'REWARD_CODE',
          CASE WHEN reward.type = 'REWARD_COIN' THEN coin.amount WHEN reward.type = 'ITEM' THEN 1 ELSE NULL END AS 'AMOUNT',
          COUNT(reward.id) AS 'COUNT'
        FROM treasure_box_winning_event e
        ${rewardJoinClause}
        LEFT JOIN treasure_box_reward_of_reward_coin coin ON reward.id = coin.id
        LEFT JOIN treasure_box_reward_of_item ti ON reward.id = ti.id
        LEFT JOIN item i ON ti.item_id = i.id
        WHERE
          ${seasonQuery}
          ${rarityQuery}
          AND e.occurred_datetime BETWEEN
            CONVERT_TZ('${rangeTarget[0]} 00:00:00', 'Asia/Tokyo', 'UTC')
            AND CONVERT_TZ('${rangeTarget[1]} 23:59:59', 'Asia/Tokyo', 'UTC')
        GROUP BY
          DAY,
          ${rarityGroupByQuery}
          reward.id,
          reward.type
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [dataRows] = yield all([
      call(
        getDataFromSxi,
        sqlQueryGoldChestTrend,
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
    ]);
    //console.log("LOG: dataRows:", dataRows);

    // 日付リスト生成
    const daysArray = H.FormatDateHelper.getDaysListBetweenDays(rangeTarget[0], rangeTarget[1]);
    const xAxisDate = [...daysArray];

    // --- キー決定（gold: 金額 / 非gold: REWARD_CODE, all: rarity|baseKey） ---
    const getBaseKey = (row) => {
      const coinMode = isGold || row.REWARD_TYPE === "REWARD_COIN";
      if (coinMode) return String(row.AMOUNT); // 50,100,500,1000...
      // アイテムはコードで集計
      return String((row.REWARD_CODE ?? "UNKNOWN_ITEM").replace("EXCHANGE_TICKET_", ""));
    };

    const getAggregateKey = (row) => {
      const baseKey = getBaseKey(row);
      if (!isAll) return baseKey;
      const rarity = String(row.RARITY || "UNKNOWN_RARITY");
      return `${rarity}|${baseKey}`;
    };

    const formatOutputLabel = (aggregateKey, rowTypeHint = "") => {
      if (!isAll) return aggregateKey;
      const [rarity, ...baseParts] = String(aggregateKey).split("|");
      const baseKey = baseParts.join("|");
      const isCoin = rowTypeHint === "REWARD_COIN" || /^\d+$/.test(baseKey);
      return isCoin ? `${baseKey}コイン (${rarity})` : `${baseKey} (${rarity})`;
    };

    // ユニークキー一覧
    const uniqueKeysSet = new Set();
    for (const r of dataRows) uniqueKeysSet.add(getAggregateKey(r));
    const uniqueKeys = Array.from(uniqueKeysSet);

    // ソート（gold: 数値昇順 / all: rarity->coin/item / 非gold: 辞書順）
    uniqueKeys.sort((a, b) => {
      if (isGold) return Number(a) - Number(b);
      if (isAll) {
        const [rarityA, ...baseA] = String(a).split("|");
        const [rarityB, ...baseB] = String(b).split("|");
        if (rarityA !== rarityB) return rarityA.localeCompare(rarityB);
        const keyA = baseA.join("|");
        const keyB = baseB.join("|");
        const numA = /^\d+$/.test(keyA);
        const numB = /^\d+$/.test(keyB);
        if (numA && numB) return Number(keyA) - Number(keyB);
        if (numA) return -1;
        if (numB) return 1;
        return keyA.localeCompare(keyB);
      }
      return String(a).localeCompare(String(b));
    });

    // --- トレンド初期化 ---
    const trendData = { date: xAxisDate };
    const outputLabelByKey = {};
    const keyTypeHint = {};
    dataRows.forEach((row) => {
      const aggregateKey = getAggregateKey(row);
      keyTypeHint[aggregateKey] = row.REWARD_TYPE;
    });
    uniqueKeys.forEach((k) => {
      const outputLabel = formatOutputLabel(k, keyTypeHint[k]);
      outputLabelByKey[k] = outputLabel;
      trendData[outputLabel] = Array(xAxisDate.length).fill(0);
    });

    // --- トレンド格納（COUNT を日別加算）---
    dataRows.forEach((item) => {
      const day = moment(item.DAY).tz("Asia/Tokyo").format("YYYY-MM-DD");
      const idx = xAxisDate.indexOf(day);
      if (idx === -1) return;
      const aggregateKey = getAggregateKey(item);
      const outputLabel = outputLabelByKey[aggregateKey];
      if (!trendData[outputLabel]) return;
      trendData[outputLabel][idx] += Number(item.COUNT) || 0;
    });

    // Redux: 日別トレンド
    if (dataRows.length === 0) {
      yield put(setPicTreeGoldChestTrend([]));
    } else {
      yield put(setPicTreeGoldChestTrend([trendData]));
    }


    // --- サマリー集計（amount = AMOUNT × COUNT の総和）---
    const summaryStats = { total: { count: 0, amount: 0 } };

    uniqueKeys.forEach((k) => {
      const outputLabel = outputLabelByKey[k];
      const totalCount = trendData[outputLabel].reduce((s, v) => s + v, 0);
      // gold も非gold も DB の AMOUNT は「単価/数量」なので、総額は (キーの単価 or 1) × COUNT
      const unitAmount = isGold ? Number(k) : 1; // 非gold/all は件数中心表示
      const totalAmount = totalCount * unitAmount;

      summaryStats[outputLabel] = { count: totalCount, amount: totalAmount };
      summaryStats.total.count += totalCount;
      summaryStats.total.amount += totalAmount;
    });

    // Redux: サマリー
    yield put(setPicTreeGoldChestStats(summaryStats));

    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
    // Keep UI from showing stale previous season data on request failure.
    yield put(setPicTreeGoldChestTrend([]));
    yield put(setPicTreeGoldChestStats({}));
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataRainbowListDetail(action) {
  try {
    yield put(setPageTopLoader(true));
    yield put(setPicTreeLatLonByPowerAssetId([]));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);

    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `tbre.game_space_id = ${ids}` // ids は "'id1'"
          : `tbre.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const whereConditions = [
      seasonQuery,
      "(tbre.rarity = 'RAINBOW' OR tbre.rarity LIKE 'SPONSOR\\_%')",
      `tbre.occurred_datetime BETWEEN CONVERT_TZ('${rangeTarget[0]} 00:00:00', 'Asia/Tokyo', 'UTC') AND CONVERT_TZ('${rangeTarget[1]} 23:59:59', 'Asia/Tokyo', 'UTC')`,
    ].filter(Boolean).join("\n          AND ");

    const sqlQueryRainbowBase = {
      query: `
        SELECT
          tbre.occurred_datetime AS 'occurred_datetime',
          tbre.treasure_box_id AS treasure_box_id,
          tbre.rarity AS rarity,
          tbr.type AS reward_type,
          CASE WHEN tbr.type = 'ITEM' THEN item.code WHEN tbr.type = 'REWARD_COIN' THEN NULL ELSE NULL END AS reward_code,
          CASE WHEN tbr.type = 'REWARD_COIN' THEN tbrcoin.amount WHEN tbr.type = 'ITEM' THEN 1 ELSE NULL END AS amount,
          tbre.player_id AS player_id,
          ps.name AS player_name
        FROM treasure_box_winning_event tbre
          STRAIGHT_JOIN treasure_box_reward tbr ON tbr.id = tbre.treasure_box_reward_id
          STRAIGHT_JOIN player_state ps ON ps.player_id = tbre.player_id
          LEFT JOIN treasure_box_reward_of_reward_coin tbrcoin ON tbr.id = tbrcoin.id
          LEFT JOIN treasure_box_reward_of_item tbritem ON tbr.id = tbritem.id
          LEFT JOIN item ON item.id = tbritem.item_id
        WHERE ${whereConditions}
        ORDER BY tbre.occurred_datetime DESC
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [
      raw
    ] = yield all([
      call(callWithTimeoutAndRetry, getDataFromSxi, [sqlQueryRainbowBase, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken], SXI_HEAVY_RETRY),
    ]);

    const source = Array.isArray(raw) ? raw : raw?.rows ?? [];
    const treasureBoxIds = Array.from(new Set(source.map((r) => r.treasure_box_id).filter(Boolean)));

    let powerAssetByTreasureBox = {};
    if (treasureBoxIds.length > 0) {
      const treasureBoxIdsIn = treasureBoxIds.map((id) => `'${id}'`).join(", ");
      const sqlQueryPowerAssetByTreasureBox = {
        query: `
          SELECT
            tbps.treasure_box_id,
            MIN(pas.power_asset_id) AS power_asset_id,
            MIN(padi.identifier_name) AS identifier_name
          FROM treasure_box_prob_setting tbps
            STRAIGHT_JOIN treasure_box_prob_setting_aggregate_bind tbpsab ON tbpsab.treasure_box_prob_setting_id = tbps.id
            STRAIGHT_JOIN power_asset_ability paai ON paai.photograph_treasure_box_prob_setting_aggregate_id = tbpsab.treasure_box_prob_setting_aggregate_id
            STRAIGHT_JOIN power_asset_setting pas ON pas.power_asset_ability_id = paai.id
            STRAIGHT_JOIN power_asset_display_properties padi ON padi.power_asset_id = pas.power_asset_id
          WHERE tbps.treasure_box_id IN (${treasureBoxIdsIn})
          GROUP BY tbps.treasure_box_id
        `,
        database: "KpiDashboard",
        type: "list",
      };

      const [rawPowerAsset] = yield all([
        call(callWithTimeoutAndRetry, getDataFromSxi, [sqlQueryPowerAssetByTreasureBox, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken], SXI_HEAVY_RETRY),
      ]);

      const powerAssetRows = Array.isArray(rawPowerAsset) ? rawPowerAsset : rawPowerAsset?.rows ?? [];
      powerAssetByTreasureBox = _.keyBy(powerAssetRows, "treasure_box_id");
    }

    const dataRows = source.map(r => [
      powerAssetByTreasureBox[r.treasure_box_id]?.identifier_name ?? "",
      powerAssetByTreasureBox[r.treasure_box_id]?.power_asset_id ?? "",
      r.reward_code ?? "",
      r.reward_type ?? "",
      r.rarity ?? "",
      Number.isFinite(r?.amount) ? r.amount : 0,
      r.player_id ?? "",
      r.player_name ?? "",
      moment.utc(r.occurred_datetime).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm:ss"),
      powerAssetByTreasureBox[r.treasure_box_id]?.power_asset_id ?? "", // 地図表示用
    ]);

    yield put(setPicTreeRainbowListDetail(dataRows));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataLatLonByPowerAssetId(action) {
  try {
    yield put(setPageTopLoader(true));
    yield put(setPicTreeLatLonByPowerAssetId([]));

    const jwtToken = yield call(getJwtToken);
    const selectedSeasonData = yield select(selectedSeason);
    const powerAssetId = action.payload;

    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `tbre.game_space_id = ${ids}` // ids は "'id1'"
          : `tbre.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const sqlQueryRainbowDetail = {
      query: `
        SELECT * FROM power_asset pa WHERE pa.id = '${powerAssetId}'
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [
      raw
    ] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [sqlQueryRainbowDetail, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    yield put(setPicTreeLatLonByPowerAssetId(raw));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export default function* watchPageSettings() {
  // Use takeLatest to avoid older season/date responses overriding newer user selections.
  yield takeLatest(PICTREE.GET_GOLD_CHEST_TREND, getPictreeDataGoldChestTrend);
  yield takeEvery(PICTREE.GET_RAINBOW_LIST_DETAIL, getPictreeDataRainbowListDetail);
  yield takeEvery(PICTREE.GET_LAT_LON_BY_POWER_ASSET_ID, getPictreeDataLatLonByPowerAssetId);
}
